import {
  ActionIcon,
  Box,
  Button,
  Center,
  Flex,
  Group,
  Indicator,
  Paper,
  ScrollArea,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { IconBell, IconUserPlus } from "@tabler/icons-react";
import { useMemo } from "react";
import QRCode from "react-qr-code";
import { client } from "@/api-client";
import type {
  EngagementWithOwnerOutput,
  GroupTrainerOutput,
  SessionWithOwnerDogDisplayListOutput,
} from "@/api-types";
import { FancyInlineLoader, FancyLoader } from "@/components/FancyLoader";
import { GroupCard } from "@/components/GroupCard";
import { Header } from "@/components/Header";
import { PersonalLinkPreview } from "@/components/PersonalLinkPreview";
import { Portrait } from "@/components/Portrait";
import { SessionCard } from "@/components/SessionCard";
import { TitleWithLink } from "@/components/TitleWithLink";
import { config } from "@/config";
import { useNotifications } from "@/hooks/useNotifications";
import { useTrainerProfileDisplay } from "@/hooks/useProfileDisplay";
import { TrainerRoutes, UniversalRoutes, useNavigator } from "@/routers/routes";

type HomePageStatuses = "REQUESTED" | "CONFIRMED" | "COMPLETED";

const SessionsList = ({
  sessions,
  showNothingIfEmpty = false,
}: {
  sessions: SessionWithOwnerDogDisplayListOutput[];
  showNothingIfEmpty?: boolean;
}) => {
  const { navigator } = useNavigator();

  if (sessions.length === 0 && showNothingIfEmpty) {
    return null;
  }

  if (sessions.length === 0) {
    return (
      <Paper
        p="md"
        withBorder
        mb="sm"
        bg="third.1"
        onClick={navigator({ path: UniversalRoutes.SESSIONS.LIST })}
      >
        <Text>No upcoming sessions. Click to view all sessions.</Text>
      </Paper>
    );
  }

  return (
    <Stack>
      {sessions.map((session) => (
        <SessionCard key={session.id} session={session} who="trainer" />
      ))}
    </Stack>
  );
};

export const InviteClients = () => {
  const { profile } = useTrainerProfileDisplay();

  const { data: bookingSettings } = client.useQuery(
    "get",
    "/trainer/profile/booking",
  );

  if (!profile || !bookingSettings) {
    return <FancyLoader />;
  }

  // const idLink = `${config.landingRoot}/join/trainer/${profile.inviteCode}`;
  // const personalLink = bookingSettings.personalLink
  //   ? `${idLink}/${bookingSettings.personalLink}`
  //   : idLink;

  const invite = () => {
    // Use the same logic as PersonalLinkPreview for the link
    const idLink = `${config.landingRoot}/join/trainer/${profile.inviteCode}`;
    const personalLink = bookingSettings.personalLink
      ? `${idLink}/${bookingSettings.personalLink}`
      : idLink;
    const shareData = {
      url: personalLink,
      text: "You can book a session with me using this link.",
      title: "Join me on Woofapy!",
    } as ShareData;

    if (window.navigator.canShare(shareData)) {
      window.navigator.share(shareData);
      return;
    }
    alert(
      "Your browser does not support sharing. Please copy the link and share it manually.",
    );
  };
  return (
    <Stack>
      <Header title={`Invite clients`} />
      <Title order={4}>Your personal Woofapy link</Title>
      <PersonalLinkPreview
        inviteCode={profile.inviteCode}
        personalLink={bookingSettings.personalLink}
      />
      <Title order={4}>Your QR Code</Title>
      <Center>
        <QRCode
          value={`${config.landingRoot}/join/trainer/${profile.inviteCode}`}
          size={200}
        />
      </Center>
      <Button onClick={invite}>Invite clients</Button>
    </Stack>
  );
};

const ClientsList = (props: {
  engagements: EngagementWithOwnerOutput[];
  engagementsWithNotifications: Set<string>;
}) => {
  const { navigator } = useNavigator();
  const { engagements } = props;
  if (engagements.length === 0) {
    return (
      <Paper
        p="md"
        withBorder
        mb="sm"
        bg="third.1"
        onClick={navigator({
          path: TrainerRoutes.PROFILE.BOOKING_SETTINGS,
        })}
      >
        <Text>
          You have no clients yet! Click to set up how dog owners can find you.
        </Text>
      </Paper>
    );
  }

  return (
    <ScrollArea w="100%">
      <Flex gap="sm">
        {engagements.map((engagement) => (
          <Stack
            key={engagement.ownerId}
            gap="0"
            onClick={navigator({
              path: TrainerRoutes.CLIENTS.DETAIL,
              params: { engagementId: engagement.id },
            })}
          >
            <Indicator
              size={15}
              color="dark-accent.3"
              offset={5}
              position="top-end"
              withBorder
              disabled={!props.engagementsWithNotifications.has(engagement.id)}
            />
            <Portrait profile={engagement.owner} size="lg" />
            <Text ta="center" fz="sm">
              {engagement.owner.firstName}
            </Text>
          </Stack>
        ))}
      </Flex>
    </ScrollArea>
  );
};

const GroupsList = (props: { groups: GroupTrainerOutput[] }) => {
  const { navigator } = useNavigator();
  const { groups } = props;

  if (groups.length === 0) {
    return (
      <Paper
        p="md"
        withBorder
        mb="sm"
        bg="third.1"
        onClick={navigator({ path: UniversalRoutes.GROUPS.LIST })}
      >
        <Text>You have no groups yet! Click to create your first group.</Text>
      </Paper>
    );
  }

  return (
    <Stack>
      {groups.slice(0, 10).map((group) => (
        <GroupCard key={group.id} group={group} />
      ))}
    </Stack>
  );
};

export function Home() {
  const { navigator } = useNavigator();
  const { unreadCount, notifications } = useNotifications();
  const engagementsWithNotifications = useMemo(
    () =>
      new Set(
        notifications
          .filter((notification) => !notification.isRead)
          .map((notification) => notification.engagementId),
      ) as Set<string>,
    [notifications],
  );

  const { profile } = useTrainerProfileDisplay();
  const { data: sessions } = client.useQuery("get", "/trainer/sessions/", {
    params: {
      query: {
        statuses: ["REQUESTED", "CONFIRMED"],
      },
    },
  });

  const { data: engagements } = client.useQuery("get", "/trainer/engagements/");
  const { data: groupsList } = client.useQuery("get", "/trainer/groups/");

  if (!profile) {
    return <FancyLoader />;
  }

  const sessionsByStatus: Record<
    HomePageStatuses,
    SessionWithOwnerDogDisplayListOutput[]
  > = {
    REQUESTED: [],
    CONFIRMED: [],
    COMPLETED: [],
  };

  sessions?.sessions.forEach((session) => {
    const sessionList = sessionsByStatus[session.status as HomePageStatuses];
    if (!sessionList) {
      return;
    }
    sessionList.push(session);
  });

  return (
    <Stack>
      <Flex gap="xs" align="center" justify="space-between">
        <Flex gap="xs" align="center">
          <Portrait profile={profile} size="lg" />
          <Stack gap="0">
            <Text fw="bold" size="xl">
              Welcome back
            </Text>
            <Text c="dimmed">{profile.firstName}</Text>
          </Stack>
        </Flex>
        <Box>
          <Group>
            <ActionIcon
              variant="transparent"
              size="lg"
              color="gray"
              onClick={navigator({ path: TrainerRoutes.INVITE })}
            >
              <IconUserPlus />
            </ActionIcon>
            <Indicator
              size={20}
              offset={5}
              position="top-end"
              color="dark-accent.3"
              withBorder
              disabled={!unreadCount}
              label={
                <Text size="xs" fw={"bold"}>
                  {unreadCount}
                </Text>
              }
            >
              <ActionIcon
                variant="transparent"
                size="lg"
                color="gray"
                onClick={navigator({
                  path: UniversalRoutes.NOTIFICATIONS.LIST,
                })}
              >
                <IconBell />
              </ActionIcon>
            </Indicator>
          </Group>
        </Box>
      </Flex>
      <TitleWithLink
        title="Upcoming sessions"
        href={UniversalRoutes.SESSIONS.LIST}
      />
      {sessions === undefined ? (
        <FancyInlineLoader />
      ) : (
        <SessionsList
          sessions={[
            ...sessionsByStatus.REQUESTED,
            ...sessionsByStatus.CONFIRMED,
          ]}
        />
      )}

      <TitleWithLink title="Your clients" href={TrainerRoutes.CLIENTS.LIST} />
      {engagements === undefined ? (
        <FancyInlineLoader />
      ) : (
        <ClientsList
          engagements={engagements.engagements}
          engagementsWithNotifications={engagementsWithNotifications}
        />
      )}

      <TitleWithLink title="Your groups" href={UniversalRoutes.GROUPS.LIST} />
      {groupsList === undefined ? (
        <FancyInlineLoader />
      ) : (
        <GroupsList
          groups={groupsList.groups
            .filter(
              (group): group is GroupTrainerOutput => "participants" in group,
            )
            .filter((group) => group.status !== "INACTIVE")}
        />
      )}
    </Stack>
  );
}
